import { isNil } from "lodash";
import type { SaleItem } from "../../../../models/saleItem";
import { isOffline } from "../../../../facade/ServiceFacade";
import { SALE_SOURCE_TYPES } from "../../../../data/constants";
import { notifySuccess } from "../../../../effects-bridge";
import {
  getLoyaltyProviderForDiscount,
  isLoyaltyPointDiscountType,
  loyaltyRefundHelper,
} from "../../../../helpers/loyaltyHelper";
import type { SaleDataType } from "../../../../models/sale";

/**
 * CROSS-PLATFORM module — `redux-store/middleware/saleUpdate/loyaltyRefundEffect`. A plain
 * `index.ts`, the same shape as its sibling `../recommendationEffect.ts`, because nothing in it
 * is platform-specific: every dependency (`isOffline`, `SALE_SOURCE_TYPES`, `notifySuccess`, the
 * loyalty predicates and the refund helper) resolves on both sides.
 *
 * 🔴 **It was `index.desktop.ts` until 2026-09-07, and that was a gap, not a design.** The file
 * cited `../cartEventEffect/cartEventHandler`'s rationale — "the other platform's `saleUpdate`
 * middleware has no counterpart" — which is a true statement about what dot-pos happened to
 * ship, not a reason mobile should go without. What it cost: this effect owns half of
 * `docs/flows/LOYALTY_REFUND.md`'s core invariant (the backend blocks N points against a
 * specific sale, and local state must agree with the block). Absent on mobile, editing a bill
 * that had points applied left those points blocked server-side against a total that no longer
 * matched, with nothing to release them.
 *
 * ⚠️ Registering it is `../index.native.ts`'s job and is easy to forget — a listener effect that
 * nothing calls is invisible. `../index.desktop.ts` and `../index.native.ts` must BOTH name it.
 *
 * ⚠️ One substitution against the app original: the success toast goes through the effects
 * bridge's `notifySuccess` where the app file called `ToastNotifySuccess`. Both apps wire that
 * bridge entry (`bootstrapCoreBridges.ts`), so the toast lands on either platform.
 *
 * ⚠️ `refundInFlight` is MODULE-LEVEL MUTABLE STATE, and moving the module into core does not
 * change that — there is still exactly one instance per bundle, which is what the guard needs.
 * It is not stored in redux deliberately: an in-flight marker that survived rehydration would
 * wedge the effect permanently.
 *
 * ⚠️ **The DotPe CRM-discount release is NOT here**, though it shares this effect's trigger and
 * its bill-change test. It lives in `../dotpeOfferReleaseEffect`, because its body calls
 * `helpers/saleHelpers`' `removeDiscountFromCart` — which exists on WEB ONLY (mobile's app has
 * no discount-removal path to uplift). Putting it here would have made the "every dependency
 * resolves on both sides" claim above false and broken the Metro build.
 */

/**
 * Guards against firing overlapping refunds. A single loyalty-points refund may be
 * in-flight at a time; while pending, further sale changes are ignored by this effect
 * (removal is committed by the refund result, which triggers a fresh saveSaleData).
 */
let refundInFlight = false;

/**
 * ONE-SHOT SUPPRESSION of the bill-change invalidation, for a caller that is about to dispatch
 * `saveSaleData` for a change it KNOWS is compatible with the currently-applied loyalty/DotPe
 * discount — "Change item" on an ALREADY-REDEEMED milestone reward, which adds the newly-picked
 * item to re-tie the reward. Without it, that add changes `saleItems` (and `billSubTotal`),
 * `hasBillChangedForLoyalty` reads it as an invalidating bill change, and the very discount the
 * item is being added for gets unblocked and stripped.
 *
 * 🔴 **It covers BOTH invalidation effects, not just this one** — `../dotpeOfferReleaseEffect`
 * asks it the same question, for the same reason it imports `hasBillChangedForLoyalty` below.
 * On DM-146 the DotPe unblock was a branch INSIDE this effect, so one early `return` at the top
 * suppressed both; here they are two listeners and a flag read by only one of them would leave
 * the coupon release — the actual thing "Change item" must not trigger — still firing.
 *
 * ⚠️ **Suppression is keyed to the ACTION, not to the first reader**, deliberately. The runners
 * call every effect with the SAME action object in one pass (`../index.desktop.ts`,
 * `../index.native.ts`), and this effect is listed before `dotpeOfferReleaseEffect` — a plain
 * `if (flag) { flag = false; return; }` in each would have the first effect consume the skip and
 * the second run anyway. Reordering the effect array must not be able to resurrect that bug.
 *
 * ⚠️ It is MODULE-LEVEL MUTABLE STATE, on the same reading as `refundInFlight` above: one
 * instance per bundle, and never in redux, where rehydration could wedge it.
 *
 * ⚠️ **Arm it immediately before the dispatch.** If the expected `saveSaleData` never arrives
 * (the caller returned early, the calculation threw), the arming survives and silently swallows
 * the NEXT genuine bill change instead. There is no timeout — the guard is cheap and the callers
 * are few, so keep the call and the dispatch adjacent.
 */
let suppressionArmed = false;
let suppressedAction: unknown = null;

export function skipNextBillChangeInvalidation(): void {
  suppressionArmed = true;
  suppressedAction = null;
}

/**
 * Is THIS `saveSaleData` pass the suppressed one? The first effect to ask claims the arming for
 * its action; every later effect in the same pass recognises that action and answers the same.
 *
 * ⚠️ Exported for `../dotpeOfferReleaseEffect` — one definition, for the same reason
 * `hasBillChangedForLoyalty` is shared rather than re-derived.
 */
export function isBillChangeInvalidationSuppressed(action: unknown): boolean {
  if (suppressionArmed) {
    suppressionArmed = false;
    suppressedAction = action;
    return true;
  }
  if (suppressedAction !== null && action === suppressedAction) return true;
  suppressedAction = null; // a later dispatch — drop the reference
  return false;
}

function getItemKey(item: SaleItem): string {
  return !isNil(item.itemNumber) ? `${item.itemId}${item.itemNumber}` : `${item.itemId}`;
}

/** True if saleItems differ by membership or quantity. */
function haveSaleItemsChanged(prevItems: SaleItem[], currItems: SaleItem[]): boolean {
  if (prevItems.length !== currItems.length) return true;
  const prevMap = new Map<string, number>();
  prevItems.forEach((item) => prevMap.set(getItemKey(item), item.quantity));
  for (const item of currItems) {
    const key = getItemKey(item);
    if (!prevMap.has(key) || prevMap.get(key) !== item.quantity) return true;
  }
  return false;
}

/** Non-loyalty bill discounts, keyed for order-independent comparison. */
function nonLoyaltyDiscountSignature(billDiscountList: any[] = []): string {
  return billDiscountList
    .filter((discount) => !isLoyaltyPointDiscountType(discount))
    .map((discount) => `${discount?.discountId ?? ""}:${discount?.mid ?? ""}:${discount?.discountRate ?? ""}`)
    .sort()
    .join("|");
}

/**
 * Whether the bill changed in a way that invalidates already-applied loyalty points and should
 * trigger their removal + refund. Fires on: saleItems change, bill subtotal change, or a change
 * to any other applied discount.
 *
 * No customer signal, by design: the refund API needs the customer on the sale, and this effect
 * runs only after the change is committed. Customer removal/edit is blocked up front instead —
 * see `isCustomerLockedByLoyalty` and `docs/flows/LOYALTY_REFUND.md`.
 *
 * ⚠️ Exported for `../dotpeOfferReleaseEffect`, which asks the same question about the same
 * `saveSaleData` action. One definition — two effects agreeing on "the bill changed" by
 * accident is exactly how one of them ends up releasing a coupon the other kept.
 */
export function hasBillChangedForLoyalty(prevSale: SaleDataType, currentSale: SaleDataType): boolean {
  const itemsChanged = haveSaleItemsChanged(prevSale?.saleItems || [], currentSale?.saleItems || []);
  const subtotalChanged = prevSale?.billSubTotal !== currentSale?.billSubTotal;
  const discountsChanged =
    nonLoyaltyDiscountSignature(prevSale?.billDiscountList) !==
    nonLoyaltyDiscountSignature(currentSale?.billDiscountList);
  return itemsChanged || subtotalChanged || discountsChanged;
}

/**
 * Listener effect for saveSaleData.
 *
 * When loyalty points are applied on the bill and the bill meaningfully changes, the points
 * must be re-validated: they are refunded (unblocked) on the backend and only then removed
 * from the sale. Removal is committed by the refund's returned sale on success; on failure
 * the points discount is left on the sale so local state stays consistent with the backend,
 * which still has the points blocked.
 *
 * This covers in-place edits of a surviving sale only. Sale teardown (reset/discard/drop,
 * including the cart being emptied) is handled by resetSaleEffect, which unblocks against
 * the discarded sale and does not write anything back.
 */
export const loyaltyRefundEffect = async (action: any, listenerApi: any) => {
  if (isBillChangeInvalidationSuppressed(action)) return;

  const prevSale = listenerApi.getOriginalState()?.saleDataStore?.saleData;
  const currentSale = listenerApi.getState()?.saleDataStore?.saleData;

  if (currentSale?.sourceInfo?.source === SALE_SOURCE_TYPES.API) return; // API-sourced sales untouched
  if (isOffline()) return; // loyalty refund is server-backed
  if (refundInFlight) return;
  // A mid change is a sale transition (reset/new sale), owned by resetSaleEffect.
  if (prevSale?.mid !== currentSale?.mid) return;

  // Points were applied before AND are still retained now. Skips the save that first applies
  // them (prev has none) and the post-refund-success save where they are already gone.
  const prevLoyalty = prevSale?.billDiscountList?.find(isLoyaltyPointDiscountType);
  const currLoyalty = currentSale?.billDiscountList?.find(isLoyaltyPointDiscountType);
  if (!prevLoyalty || !currLoyalty) return;

  if (!hasBillChangedForLoyalty(prevSale, currentSale)) return;

  const loyaltyProviders =
    listenerApi.getState()?.businessStore?.businessData?.loyaltyProviders || [];
  const provider: any = getLoyaltyProviderForDiscount(loyaltyProviders, currLoyalty);

  refundInFlight = true;
  loyaltyRefundHelper(
    provider?.mid,
    (res: any) => {
      refundInFlight = false;
      // On success loyaltyRefundHelper dispatches saveSaleData(res.sale) (loyalty removed);
      // on failure it toasts "Failed to remove loyalty points" and nothing is removed.
      if (res?.refundSuccess) {
        notifySuccess("Loyalty points removed successfully");
      }
    },
    false,
  );
};
